/**
 * React Starter Kit (https://www.reactstarterkit.com/)
 *
 * Copyright © 2014-present Kriasoft, LLC. All rights reserved.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE.txt file in the root directory of this source tree.
 */

import React from 'react';
import withStyles from 'isomorphic-style-loader/lib/withStyles';
import s from './Education.css';
import education1 from './education_1.png';
import education12x from './education_1@2x.png';
import education2 from './education_2.png';
import education22x from './education_2@2x.png';
import education3 from './education_3.png';
import education32x from './education_3@2x.png';
import education4 from './education_4.png';
import education42x from './education_4@2x.png';
import education5 from './education_5.png';
import education52x from './education_5@2x.png';
import education6 from './education_6.png';
import education62x from './education_6@2x.png';
import education7 from './education_7.png';
import education72x from './education_7@2x.png';
import education8 from './education_8.png';
import education82x from './education_8@2x.png';
import education9 from './education_9.png';
import education92x from './education_9@2x.png';
import education10 from './education_10.png';
import education102x from './education_10@2x.png';
import education11 from './education_11.png';
import education112x from './education_11@2x.png';

class Education extends React.Component {
  render() {
    return (
      <div className={s.root}>
        <div className={s.container}>
          <div className={s.section}>
            <div className={s.top}>
              <img
                src={education1}
                srcSet={`${education12x} 2x`}
                width="1000"
                height="650"
                alt="The science behind habits"
              />
              <h1 className={s.title}>The science behind habits</h1>
              <p className={s.subtitle}>
                People have been pondering about the power of habits from the
                time of Aristotle. Believe it or not, there's actually a science
                behind how habits work. Here we'll explore how habits work and
                how we can change them.
              </p>
            </div>
            <div className={s.bottom}>
              <img
                src={education4}
                srcSet={`${education42x} 2x`}
                width="1000"
                height="650"
                alt="Cue, Craving, Response, Reward"
              />
              <p className={s.bodytitle}>The habit loop</p>
              <p className={s.body}>
                When performing a habit, whether good or bad, you go through the
                same pattern. This pattern can be summed up in four steps. These
                four steps can be described as a feedback loop.
              </p>
              <p className={s.body}>
                Myself, I have a habit of spending too much time playing card games. Actually, I've played so much Solitaire, Heart and Spades that I ended up creating a website where you can <a href="https://online-solitaire.com/">play solitaire online</a> and another one where you can <a href="https://worldofcardgames.com/">play multiplayer card games</a>. A habit loop for my solitaire habit might look like this:
              </p>
              <p className={s.bodytitle}>1: Cue</p>
              <p className={s.body}>
                Something, mostly an external thing, suggests there's a reward to be found. In this case, it might be the solitaire bookmark I have in my browser.
              </p>
              <p className={s.bodytitle}>2: Craving</p>
              <p className={s.body}>
                A motivation builds up that makes it difficult for you to ignore the cue. My fingers are itching to play a game of <a href="https://worldofcardgames.com/hearts">Hearts</a>, <a href="https://worldofcardgames.com/spades">Spades</a>, <a href="https://online-solitaire.com/freecell">FreeCell</a> or <a href="https://online-solitaire.com/spider-solitaire">Spider Solitaire</a>.
              </p>
              <p className={s.bodytitle}>3: Response</p>
              <p className={s.body}>
                The craving is acted upon. I click that darn bookmark to get my daily dose of solitaire.
              </p>
              <p className={s.bodytitle}>4: Reward</p>
              <p className={s.body}>
                Satisfaction is felt when the craving is fulfilled. In this case, I'm thrown into an exciting solitaire game.
              </p>
              <p className={s.body}>
                The above example might not be the habit you're trying to
                create, but good habits work just the same. You might come home
                from sitting at the office all day and notice the running shoes
                in your hallway. You get a craving for the satisfying feeling of
                doing physical exercise. You put on the running shoes and get
                the satisfying feeling of doing physical exercise.
              </p>
              <p className={s.body}>
                You go through the pattern above whether you've done that action
                one time or many times. Which is why the pattern can be
                described as a feedback loop or habit loop. This is the loop
                we're trying to take advantage of when we're trying to build
                good habits or get rid of bad ones.
              </p>
            </div>
          </div>

          <div className={s.section}>
            <div className={s.top}>
              <img
                src={education2}
                srcSet={`${education22x} 2x`}
                width="1000"
                height="650"
                alt="A system for changing habits"
              />
              <h1 className={s.title}>A system for changing habits</h1>
              <p className={s.subtitle}>
                People have a hard time creating good habits because they don't
                know how habits work. With that knowledge, we can set up a
                simple system that'll make it easy for us to stick with our
                habits. Let's explore how good habits a build and how to get rid
                of bad habits.
              </p>
            </div>
            <div className={s.bottom}>
              <img
                src={education5}
                srcSet={`${education52x} 2x`}
                width="1000"
                height="650"
                alt="Make the habit obvious to trigger the cue"
              />
              <p className={s.bodytitle}>A habit-changing pattern</p>
              <p className={s.body}>
                As there are four steps in the habit loop, there are four steps
                you can take to create or get rid of a habit. These steps
                correlate with the four steps of the habit loop.
              </p>
              <p className={s.bodytitle}>
                1: Make the habit obvious to trigger the cue
              </p>
              <p className={s.body}>
                You only have a certain amount of willpower you can spend. After
                you've spent it, you'll start to give in to the cues you're
                exposed to. So you'll want to make the cues for your good habits
                obvious and your bad habits invisible. To stop drinking soda,
                get rid of soda in the house, to make the "drink soda" cue
                invisible. Building a good habit can also be helped by changing
                your environment. If you want to start running, keep your shoes
                in the hallway along with your other running gear, so it's
                always accessible and visible. You can also stack habits on top
                of each other, so when you've done one specific habit, like
                brushing your teeth, you go straight on to another habit, like
                making your bed. Another helpful thing you can do is to be
                specific when you want to do your habit. In Habitual you
                can set reminders for your habits, to get a notification (or
                cue), that it's time to do the habit.
              </p>
              <p className={s.bodytitle}>
                2: Make the habit attractive to build the craving
              </p>
              <p className={s.body}>
                Habits aren't always inherently pleasurable, and when we do
                things that aren't pleasurable, we simply spend our willpower.
                There are a few things you can do to counteract this. Depending
                on the task, you might be able to do something pleasurable while
                you do it, like listening to music you like while doing
                exercises you don't like. This gets the brain to associate the
                habit you otherwise don't like with something you do like. You
                can also reward yourself for doing the habit, which gives you a
                nice "carrot" to look forward to in the end. You might even be
                able to swap a bad habit with a good one, like going for a run
                instead of drinking to burn off steam. To quit a habit, make it
                unattractive.
              </p>
              <p className={s.bodytitle}>
                3: Make the habit easy to make the response simple to act on
              </p>
              <p className={s.body}>
                The easier an action is to do, the more likely it is that you'll
                do it. Remember, that you only have a certain amount of
                willpower and once you've spent that, you'll have a hard time
                doing actions you otherwise wouldn't do. So if an action is hard
                to do, chances are that you won't do it. Try to reduce friction
                in the habits you want to build. For example, go to the gym on
                your way home from work, when you're already on the move. Forget
                about doing things perfectly when you're building trying to
                build a habit. It's much more important that you're doing your
                habit consistently than you're doing it perfectly. When starting
                a new habit, try not to aim too high. If possible, try to keep
                the duration of your habit below 2 minutes. Doing things
                consistently is everything when building habits. To get rid of a
                habit, try to add friction. For example, if you want to limit
                the time you spend on social media, then delete them from your
                phone, so you have to use your computer.
              </p>
              <p className={s.bodytitle}>
                4: Make the habit rewarding so you'll want to do it again
              </p>
              <p className={s.body}>
                The first three steps increase the chances that you'll perform
                the habit you want to build. Making the reward bigger for doing
                those actions, make you more likely to want to repeat them, thus
                creating a lasting habit. The easiest way to make the habit feel
                rewarding is to use a habit tracker like Habitual. After you've
                started a habit, you'll see a timeline of rewarding checkmarks,
                each indicating a day where you've done all the habits you set
                out to do. Creating this chain of accomplished habits becomes a
                rewarding visual cue that you'll have a harder time breaking.
              </p>
            </div>
          </div>

          <div className={s.section}>
            <div className={s.top}>
              <img
                src={education3}
                srcSet={`${education32x} 2x`}
                width="1000"
                height="650"
                alt="Tips and tricks"
              />
              <h1 className={s.title}>Tips and tricks</h1>
              <p className={s.subtitle}>
                There are lots of tips and tricks you can apply when you're
                changing your habits. Here we've gathered some of the best
                knowledge available.
              </p>
            </div>
            <div className={s.bottom}>
              <img
                src={education9}
                srcSet={`${education92x} 2x`}
                width="1000"
                height="650"
                alt="Your habits shape your identity and vice versa"
              />
              <p className={s.bodytitle}>
                Your habits shape your identity and vice versa
              </p>
              <p className={s.body}>
                Behavioral change can be divided into 3 layers. You can have a
                change in your outcomes, what you get, a change in your
                process, what you do, and a change in your identity, what you
                believe. The most durable type of behavioral change is a
                change to your identity. Most people make the mistake of
                trying to change what they get or what they do when who they
                are is what they should focus on. Change your identity and
                your habits will change with it. It also goes the other way,
                though. Make a change with what you do for a long enough time,
                and it will become a part of your identity.
              </p>
              <img
                src={education6}
                srcSet={`${education62x} 2x`}
                width="1000"
                height="650"
                alt="Focus on systems - not goals"
              />
              <p className={s.bodytitle}>Focus on systems - not goals</p>
              <p className={s.body}>
                When people look to make a change in their life, they tend to
                focus on the change they want to achieve, the goal, and often
                forget about the system that will lead to that goal. This is
                one of the main reasons people have such a hard time changing
                their habits. Systems are the processes that will lead you to
                your goal and setting up a system that will help you create a
                stable habit is vital if you want to make a change. People
                often think that the process of creating a habit is a linear
                experience, where it's actually an exponential one. If you've
                ever tried to create a new habit, you've probably experienced
                the difficulty of doing it consistently at the start. That's
                because you're in the "valley of disappointment" and don't
                feel the full benefits of the results yet.
              </p>
              <img
                src={education7}
                srcSet={`${education72x} 2x`}
                width="1000"
                height="650"
                alt="There's a world of difference between one percent better or worse each day"
              />
              <p className={s.bodytitle}>
                There's a world of difference between one percent better or
                worse each day
              </p>
              <p className={s.body}>
                Becoming one percent better at something each day doesn't
                sound like a lot, but that one percent compounds over time and
                becomes exponential growth. Becoming one percent worse at
                something each day has a similar effect in the opposite
                direction. That's why when you're trying to create a new
                habit, all your focus should be on doing that habit as much as
                possible. In other words, it's quantity over quality. It's
                better to meditate 10 minutes every day than 1 hour at the end
                of the week if you want to get into the habit of meditating.
              </p>
              <p className={s.bodytitle}>Doing habits one after another</p>
              <p className={s.body}>
                One little trick you can use to lure yourself into doing your
                habits is to stack them up against each other. For example,
                you could have a morning routine, where you'd brush your
                teeth, and after that, you'd prepare your lunch, and after
                that, you'd meditate for 10 minutes. Finishing one habit will
                often give you a kick that will make you want to start
                another.
              </p>
              <img
                src={education10}
                srcSet={`${education102x} 2x`}
                width="1000"
                height="650"
                alt="The Goldilocks rule"
              />
              <p className={s.bodytitle}>The Goldilocks rule</p>
              <p className={s.body}>
                When setting up the habit you want to build, make sure to
                think about the Goldilocks rule. It's a rule, or guideline,
                that describes the sweet spot for human activity. In a
                nutshell, it states that if people are too bored or too
                challenge by what they're doing, they not as likely to do it.
                In other words, when starting out a new habit, you don't want
                to set the bar too high or too low.
              </p>
              <img
                src={education11}
                srcSet={`${education112x} 2x`}
                width="1000"
                height="650"
                alt="A good day often starts with a few vital decisions"
              />
              <p className={s.bodytitle}>
                A good day often starts with a few vital decisions
              </p>
              <p className={s.body}>
                The US Marines are taught to make their bed every morning.
                This might seem like a silly thing to spend time on, but
                they've implemented this habit for a very good reason. Whether
                your day becomes a good day or a bad day often comes back to a
                few vital decisions at the start of the day. You can look upon
                it as a decision-tree that grows larger as the day passes.
                Making the right decisions at the beginning of the day will not
                only give you a headstart making that day a good day, but it
                will also give you the kick you need to keep making the right
                decisions during the rest of the day.
              </p>
              <img
                src={education8}
                srcSet={`${education82x} 2x`}
                width="1000"
                height="650"
                alt="A habit isn't formed in a day"
              />
              <p className={s.bodytitle}>A habit isn't formed in a day</p>
              <p className={s.body}>
                On paper, you might form a habit from one day to another, but
                in reality, it takes time to form a new habit. When an action
                becomes so automatic for you that you don't really give it any
                thought before doing it, then it's a habit. Getting to that
                point of automaticity is something you build up by doing that
                particular action again and again. Once you've done that
                action enough times, it becomes a habit. You can do things like creating a dynamic wallpaper for Mac from <a href="https://dynamicwallpaper.club/">Dynamic Wallpaper Club</a> to keep track of your day. A dynamic wallpaper can help you with your habits by reminding you about the time throughout the day in a non-intrusive manner.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default withStyles(s)(Education);
